/**
 * Avatar atom - User profile image or initials.
 */
import { getInitials } from '../utils';

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  isOnline?: boolean;
  /**
   * Fill the parent instead of using the size table. StoryRing needs this:
   * its `lg` is 64px while Avatar's `lg` is 48px, and the mismatch drew a
   * floating ring with a cream moat around a too-small avatar (BUG-023).
   */
  fill?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

const textSizes = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
};

// Five retro fills. A person keeps the same one everywhere in the app because
// it comes from their name, not from where they happen to be rendered.
const avatarColors = [
  'bg-avatar-1',
  'bg-avatar-2',
  'bg-avatar-3',
  'bg-avatar-4',
  'bg-avatar-5',
];

export function avatarColorFor(name?: string): string {
  if (!name) return avatarColors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

const indicatorSizes = {
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
  xl: 'h-4 w-4',
};

export default function Avatar({
  src,
  alt,
  name,
  size = 'md',
  isOnline,
  fill = false,
  className = '',
}: AvatarProps) {
  const initials = name ? getInitials(name) : '?';
  const circleClasses = fill ? `h-full w-full ${textSizes[size]}` : sizeClasses[size];

  return (
    <div className={`relative ${fill ? 'block h-full w-full' : 'inline-block'} ${className}`}>
      {src ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className={`${circleClasses} rounded-full object-cover`}
        />
      ) : (
        <div
          className={`${circleClasses} flex items-center justify-center rounded-full ${avatarColorFor(name)} font-medium text-text-inverse`}
        >
          {initials}
        </div>
      )}

      {isOnline !== undefined && (
        <span
          className={`absolute bottom-0 right-0 block ${indicatorSizes[size]} rounded-full ring-2 ring-background ${
            isOnline ? 'bg-teal-light' : 'bg-text-muted'
          }`}
          aria-label={isOnline ? 'Online' : 'Offline'}
        />
      )}
    </div>
  );
}
